<template>
  <div class="w-52">
    <div class="mb-2 text-xs font-semibold text-gray-400 uppercase">
      {{ $t("BORROWER_PLATFORM.TILES_CONTENT") }}
    </div>
    <div
      class="flex items-center rounded-5 border border-gray-200 bg-white px-6 py-[5px]"
    >
      <lf-dropdown
        :value="localValue.image"
        :options="imageOptionsFormatted"
        :icons="imageIcons"
        :disabled="disabled"
        class="flex-none"
        data-cy="tiles-content-image-dropdown"
        :full-width="false"
        :search-enabled="false"
        name="image-content"
        menu-width="w-10"
        @change="handleImageChange"
      >
        <template #toggle-button="{ toggle, menuOpen }">
          <div class="flex items-center space-x-1" @click="toggle">
            <icon-base
              v-if="imageIcons[localValue.image]?.icon"
              :icon="imageIcons[localValue.image].icon as any"
            />
            <icon-chevron-down
              class="transform transition-transform"
              :class="{ 'rotate-180': !menuOpen }"
            />
          </div>
        </template>
      </lf-dropdown>
      <div class="mx-4 h-8 w-px bg-gray-200" />
      <lf-dropdown
        :value="localValue.header"
        :options="headerOptionsFormatted"
        :icons="headerIcons"
        :disabled="disabled"
        class="flex-none"
        data-cy="tiles-content-header-dropdown"
        :full-width="false"
        :search-enabled="false"
        name="header-style"
        menu-width="w-12"
        @change="handleHeaderChange"
      >
        <template #toggle-button="{ toggle, menuOpen }">
          <div class="flex items-center space-x-1" @click="toggle">
            <icon-base
              v-if="headerIcons[localValue.header]?.icon"
              :icon="headerIcons[localValue.header].icon as any"
            />
            <icon-chevron-down
              class="transform transition-transform"
              :class="{ 'rotate-180': !menuOpen }"
            />
          </div>
        </template>
      </lf-dropdown>
      <div class="mx-4 h-8 w-px bg-gray-200" />
      <lf-dropdown
        :value="localValue.button"
        :options="buttonOptionsFormatted"
        :icons="buttonIcons"
        :disabled="disabled"
        class="flex-none"
        data-cy="tiles-content-button-dropdown"
        :full-width="false"
        :search-enabled="false"
        name="button-type"
        menu-width="w-10"
        @change="handleButtonChange"
      >
        <template #toggle-button="{ toggle, menuOpen }">
          <div class="flex items-center space-x-1" @click="toggle">
            <icon-base
              v-if="buttonIcons[localValue.button]?.icon"
              :icon="buttonIcons[localValue.button].icon as any"
            />
            <icon-chevron-down
              class="transform transition-transform"
              :class="{ 'rotate-180': !menuOpen }"
            />
          </div>
        </template>
      </lf-dropdown>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

import {
  BorrowerPlatformTileHeaderType,
  TileActionType,
  TileImageType,
  type TilesContentControlValue,
} from "@/enums/bpCustomisation";

import IconButton from "@/components/icons/borrowerPlatform/borrowerTabs/IconButton.vue";
import IconH1 from "@/components/icons/borrowerPlatform/borrowerTabs/IconH1.vue";
import IconH2 from "@/components/icons/borrowerPlatform/borrowerTabs/IconH2.vue";
import IconH3 from "@/components/icons/borrowerPlatform/borrowerTabs/IconH3.vue";
import IconH4 from "@/components/icons/borrowerPlatform/borrowerTabs/IconH4.vue";
import IconIllustration from "@/components/icons/borrowerPlatform/borrowerTabs/IconIllustration.vue";
import IconImg from "@/components/icons/borrowerPlatform/borrowerTabs/IconImg.vue";
import IconLink from "@/components/icons/borrowerPlatform/borrowerTabs/IconLink.vue";
import IconNone from "@/components/icons/borrowerPlatform/borrowerTabs/IconNone.vue";
import IconChevronDown from "@/components/icons/deals/IconChevronDown.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

const props = defineProps<{
  modelValue: TilesContentControlValue;
  disabled?: boolean;
}>();

const { disabled = false } = props;

const emit = defineEmits<{
  "update:modelValue": [value: TilesContentControlValue];
}>();

const localValue = ref({ ...props.modelValue });

const handleImageChange = (value: string | number) => {
  localValue.value.image = value as TileImageType;
  emit("update:modelValue", { ...localValue.value });
};

const handleHeaderChange = (value: string | number) => {
  localValue.value.header = value as BorrowerPlatformTileHeaderType;
  emit("update:modelValue", { ...localValue.value });
};

const handleButtonChange = (value: string | number) => {
  localValue.value.button = value as TileActionType;
  emit("update:modelValue", { ...localValue.value });
};

const imageOptions = [
  {
    value: TileImageType.ILLUSTRATION,
    label: "",
    icon: IconIllustration,
  },
  { value: TileImageType.IMAGE, label: "", icon: IconImg },
  { value: TileImageType.NONE, label: "", icon: IconNone },
];
const headerOptions = [
  { value: BorrowerPlatformTileHeaderType.H1, label: "", icon: IconH1 },
  { value: BorrowerPlatformTileHeaderType.H2, label: "", icon: IconH2 },
  { value: BorrowerPlatformTileHeaderType.H3, label: "", icon: IconH3 },
  { value: BorrowerPlatformTileHeaderType.H4, label: "", icon: IconH4 },
];
const buttonOptions = [
  { value: TileActionType.BUTTON, label: "", icon: IconButton },
  {
    value: TileActionType.REDIRECT_LINK,
    label: "",
    icon: IconLink,
  },
  { value: TileActionType.NONE, label: "", icon: IconNone },
];

const imageOptionsFormatted = Object.fromEntries(
  imageOptions.map((option) => [option.value, option.label])
);

const headerOptionsFormatted = Object.fromEntries(
  headerOptions.map((option) => [option.value, option.label])
);

const buttonOptionsFormatted = Object.fromEntries(
  buttonOptions.map((option) => [option.value, option.label])
);

const imageIcons = Object.fromEntries(
  imageOptions.map((option) => [option.value, { icon: option.icon }])
);

const headerIcons = Object.fromEntries(
  headerOptions.map((option) => [option.value, { icon: option.icon }])
);

const buttonIcons = Object.fromEntries(
  buttonOptions.map((option) => [option.value, { icon: option.icon }])
);

watch(
  () => props.modelValue,
  (val) => {
    localValue.value = { ...val };
  },
  { immediate: true }
);
</script>
<style scoped>
@reference "@/assets/tailwind.css";
:deep(.dropdown-option) {
  text-align: center !important;
  justify-content: center !important;
  padding-right: 0 !important;
}
:deep(.dropdown-option .capitalize) {
  display: none !important;
}
</style>
